/**
 * Per-line tax display (matches backend lineItemTax / quote_items).
 */

export function normalizeLineTaxModeUi(v) {
  const s = String(v == null || v === '' ? 'none' : v).trim().toLowerCase();
  if (s === 'percent') return 'percent';
  if (s === 'fixed') return 'fixed';
  return 'none';
}

export function normalizeTaxBasisUi(v) {
  const s = String(v == null || v === '' ? 'per_line' : v).trim().toLowerCase();
  if (s === 'per_line' || s === 'line' || s === 'lines') return 'per_line';
  return 'document';
}

export function computeLineItemTaxAmount(lineSubtotal, row) {
  const sub = Number(lineSubtotal) || 0;
  const mode = normalizeLineTaxModeUi(row?.line_tax_mode);
  if (mode === 'percent') {
    const p = parseFloat(row?.line_tax_percent);
    if (!Number.isFinite(p) || p < 0) return 0;
    return Math.round(sub * (p / 100) * 100) / 100;
  }
  if (mode === 'fixed') {
    const f = parseFloat(row?.line_tax_fixed_amount);
    if (!Number.isFinite(f) || f < 0) return 0;
    return Math.round(f * 100) / 100;
  }
  return 0;
}

/** Pre-tax line extension + per-line tax (for “Subtotal” / line total column when tax_basis is per_line). */
export function computeLineTotalWithPerLineTax(lineSubtotal, row) {
  const sub = Math.round((Number(lineSubtotal) || 0) * 100) / 100;
  return Math.round((sub + computeLineItemTaxAmount(sub, row)) * 100) / 100;
}

/** Tax rate in percent points, rounded to 2 decimal places (e.g. 8.25). */
function roundTaxPercentPoints(p) {
  if (!Number.isFinite(p) || p < 0) return null;
  return Math.round(p * 100) / 100;
}

/**
 * Display tax rate (percent points): integers show with no decimals; otherwise max 2 decimal places (no trailing zeros).
 */
export function formatTaxPercentDisplayValue(raw) {
  if (raw === '' || raw == null) return '';
  const t = String(raw).trim();
  if (t === '' || t === '.' || t === '-') return '';
  const p = parseFloat(t.replace(/,/g, ''));
  if (!Number.isFinite(p) || p < 0) return '';
  const r = Math.round(p * 100) / 100;
  const asInt = Math.round(r);
  if (Math.abs(r - asInt) < 1e-9) return String(asInt);
  return String(parseFloat(r.toFixed(2)));
}

/**
 * While typing document / footer "Rate %": at most 2 digits before the decimal and 2 after.
 * Allows intermediate states like "" or "1." or ".".
 */
export function sanitizeTaxPercentInputValue(raw) {
  const s = String(raw ?? '').replace(/,/g, '');
  let intPart = '';
  let fracPart = '';
  let sawDot = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === '.') {
      if (!sawDot) sawDot = true;
      continue;
    }
    if (ch < '0' || ch > '9') continue;
    if (!sawDot) {
      if (intPart.length < 2) intPart += ch;
    } else if (fracPart.length < 2) {
      fracPart += ch;
    }
  }
  if (!sawDot) return intPart;
  return intPart + '.' + fracPart;
}

function formatTaxPercentPointsString(p) {
  const r = roundTaxPercentPoints(p);
  return r === null ? '' : formatTaxPercentDisplayValue(r);
}

/** Derive percent points from tax $ and subtotal, rounded to 2 decimals. */
function roundPercentRatio(amount, subtotal) {
  const sub = Number(subtotal) || 0;
  const amt = Number(amount);
  if (!Number.isFinite(amt) || amt < 0 || sub <= 0) return '';
  return formatTaxPercentPointsString((amt / sub) * 100);
}

/**
 * Patch object for edited line state: sync Tax % → store as percent mode.
 * Empty input clears line tax.
 */
export function lineTaxPatchFromPercentInput(lineSubtotal, percentStr) {
  const t = String(percentStr ?? '').trim();
  if (t === '' || t === '.' || t === '-') {
    return { line_tax_mode: 'none', line_tax_percent: '', line_tax_fixed_amount: '' };
  }
  const p = parseFloat(t);
  if (!Number.isFinite(p) || p < 0) {
    return null;
  }
  const r = roundTaxPercentPoints(p);
  return {
    line_tax_mode: 'percent',
    line_tax_percent: r === null ? '' : formatTaxPercentDisplayValue(r),
    line_tax_fixed_amount: ''
  };
}

/**
 * Patch from Tax $ input: derive % when subtotal > 0, else fixed amount.
 */
export function lineTaxPatchFromAmountInput(lineSubtotal, amountStr) {
  const t = String(amountStr ?? '').trim();
  if (t === '' || t === '.' || t === '-') {
    return { line_tax_mode: 'none', line_tax_percent: '', line_tax_fixed_amount: '' };
  }
  const amt = parseFloat(t);
  if (!Number.isFinite(amt) || amt < 0) {
    return null;
  }
  const sub = Number(lineSubtotal) || 0;
  if (sub > 0.000001) {
    const pctStr = roundPercentRatio(amt, sub);
    return { line_tax_mode: 'percent', line_tax_percent: pctStr, line_tax_fixed_amount: '' };
  }
  return { line_tax_mode: 'fixed', line_tax_percent: '', line_tax_fixed_amount: String(Math.round(amt * 100) / 100) };
}

/**
 * Next Tax % after ArrowUp/ArrowDown: round the displayed rate to the nearest whole percent, then ±1 (min 0).
 * Manual typing is unchanged (use step="any" on the input to allow decimals like 8.25).
 */
export function lineTaxPercentAfterArrowStep(row, lineSubtotal, direction) {
  const disp = lineTaxPercentFieldValue(row, lineSubtotal);
  let cur = 0;
  if (disp !== '' && disp != null) {
    const p = parseFloat(String(disp));
    if (Number.isFinite(p)) cur = p;
  }
  const base = Math.round(cur);
  const next = direction === 'up' ? base + 1 : Math.max(0, base - 1);
  return String(next);
}

/** MUI TextField onKeyDown: integer step on arrows; does not block decimal entry. */
export function handleLineTaxPercentArrowKeyDown(e, row, lineSubtotal, onLineTaxPatch) {
  if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
  e.preventDefault();
  const direction = e.key === 'ArrowUp' ? 'up' : 'down';
  const nextStr = lineTaxPercentAfterArrowStep(row, lineSubtotal, direction);
  const patch = lineTaxPatchFromPercentInput(lineSubtotal, nextStr);
  if (patch) onLineTaxPatch(patch);
}

/** Prevent scroll-from-wheel changing type="number" while the input is focused. */
export function lineTaxPercentInputPropsExtra() {
  return {
    onWheel: (e) => {
      if (e.currentTarget === document.activeElement) e.preventDefault();
    }
  };
}

/** Hide native spin buttons (they use browser step, not our integer ArrowUp/Down behavior). */
export const lineTaxPercentNumberFieldSx = {
  '& .MuiOutlinedInput-input::-webkit-outer-spin-button, & .MuiOutlinedInput-input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0
  },
  '& .MuiOutlinedInput-input[type=number]': {
    MozAppearance: 'textfield'
  }
};

/** Quote/Order line tables: Tax % beside % (typical rates like 6.14; avoids huge empty width). */
export const lineItemTaxPercentEditFieldSx = {
  ...lineTaxPercentNumberFieldSx,
  width: '100%',
  '& .MuiOutlinedInput-root': { flexWrap: 'nowrap' },
  '& .MuiOutlinedInput-input': {
    minWidth: '4.5ch',
    boxSizing: 'content-box'
  }
};

/** Quote/Order line tables: Tax $ beside $ (e.g. 9,999.99 with grouping). */
export const lineItemTaxDollarsEditFieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': { flexWrap: 'nowrap' },
  '& .MuiOutlinedInput-input': {
    minWidth: '11ch',
    boxSizing: 'content-box'
  }
};

/** Quote/Order/QuoteNew footer: tax Rate % / Amount — no extra vertical margin, tighter input padding. */
export const documentFooterTaxFieldSx = {
  ...lineTaxPercentNumberFieldSx,
  m: 0,
  mt: 0,
  mb: 0,
  '& .MuiOutlinedInput-root': {
    marginTop: 0
  },
  '& .MuiOutlinedInput-input': {
    py: '4px',
    px: '8px',
    fontSize: '0.8125rem',
    lineHeight: 1.35
  }
};

/**
 * Document footer “Rate” (order/quote tax %): compact (~½ of former ~88px; tighter than ⅓ would clip XX.XX + %).
 * Pair with a fixed `%` end adornment (label removed in UI).
 */
export const documentFooterTaxRatePercentFieldSx = {
  ...documentFooterTaxFieldSx,
  width: '3.5rem',
  maxWidth: '3.75rem',
  flexShrink: 0,
  '& .MuiOutlinedInput-root': {
    marginTop: 0,
    flexWrap: 'nowrap',
    paddingRight: '2px'
  },
  '& .MuiOutlinedInput-input': {
    py: '4px',
    pl: '6px',
    pr: '2px',
    fontSize: '0.8125rem',
    lineHeight: 1.35,
    textAlign: 'left',
    minWidth: '2.25ch',
    maxWidth: '7ch',
    width: '7ch',
    boxSizing: 'content-box'
  }
};

/** Value for Tax % field (view or edit) from row + subtotal. */
export function lineTaxPercentFieldValue(row, lineSubtotal) {
  const mode = normalizeLineTaxModeUi(row?.line_tax_mode);
  if (mode === 'percent') {
    const s = row?.line_tax_percent;
    if (s == null || String(s).trim() === '') return '';
    const p = parseFloat(String(s).trim());
    if (!Number.isFinite(p) || p < 0) return '';
    return formatTaxPercentPointsString(p);
  }
  if (mode === 'fixed') {
    const f = parseFloat(row?.line_tax_fixed_amount);
    if (!Number.isFinite(f) || f < 0) return '';
    const sub = Number(lineSubtotal) || 0;
    if (sub > 0.000001) return roundPercentRatio(f, sub);
    return '';
  }
  return '';
}

/** Short label for read-only / print (e.g. 8.25%). */
export function lineTaxPercentLabel(row, lineSubtotal) {
  const v = lineTaxPercentFieldValue(row, lineSubtotal);
  if (v === '') return '—';
  return `${v}%`;
}

/** Per-line tax amount: always $X,XXX.00 (2 decimal places). */
export function formatLineItemTaxDollars(amount) {
  const n = Math.round((Number(amount) || 0) * 100) / 100;
  if (!Number.isFinite(n)) return '$0.00';
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
